import { useState, useEffect, useMemo } from "react";
import { Users, Search, Pencil, UserX, UserCheck, Scissors, ChevronDown, UserPlus, UserCog, Plus, History } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "../../../components/ui/Badge";
import { LoadingSpinner } from "../../../components/ui/LoadingSpinner";
import { EmptyState } from "../../../components/ui/EmptyState";
import { Modal } from "../../../components/ui/Modal";
import { ToastContainer } from "../../../components/ui/Toast";
import { useToast } from "../../../hooks/useToast";
import { useUsers, useDeactivateUser, useReactivateUser } from "../../../hooks/useUsers";
import { useBizUsers } from "../../../hooks/useBizAdmin";
import { useAuth } from "../../../hooks/useAuth";
import { bizRoleApi } from "../../../lib/roleApi";
import { formatCurrency, formatDate } from "../../../utils/format";
import type { EmployeeProfile } from "../../../types/index";
import { AddUserModal } from "./AddUserModal";
import { AddExistingMemberModal } from "./AddExistingMemberModal";
import { EditUserDrawer } from "./EditUserDrawer";
import { AuditHistoryModal } from "../../../components/AuditHistoryModal";
import { auditApi } from "../../../lib/auditApi";

// ── Confirm action modal ──────────────────────────────────────────────────────

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  confirmLabel: string;
  isDanger?: boolean;
  isPending?: boolean;
}

function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel,
  isDanger = false,
  isPending = false,
}: ConfirmModalProps) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="sm">
      <p className="text-sm text-[#64748b] mb-5">{description}</p>
      <div className="flex justify-end gap-3">
        <button
          onClick={onClose}
          disabled={isPending}
          className="rounded-lg border border-[#e2e8f0] px-4 py-2 text-sm font-medium text-[#374151] hover:bg-[#f8fafc] transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={isPending}
          className={[
            "rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors disabled:opacity-60",
            isDanger
              ? "bg-[#dc2626] hover:bg-[#b91c1c]"
              : "bg-[#2447d9] hover:bg-[#1e3a8a]",
          ].join(" ")}
        >
          {isPending ? "Please wait…" : confirmLabel}
        </button>
      </div>
    </Modal>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function UsersPage() {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const { isAdmin, activeBusinessId, user: authUser } = useAuth();

  // Scope by the business selected in the TopBar switcher (super-admin & business
  // admins alike). No active business (rare) falls back to the global user list.
  const bizId = activeBusinessId ?? "";

  // Debounce: only fire the API call 400 ms after the user stops typing
  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(id);
  }, [searchInput]);

  const adminUsers = useUsers(search, isAdmin && !bizId);
  const bizUsers = useBizUsers(bizId);

  // Use biz-scoped data whenever there is an active business (covers biz admin,
  // non-biz-admin with admin:users, and super-admin with a biz selected).
  // Only fall back to the super-admin global endpoint when there is no active biz.
  const useBizData = !!bizId;

  const {
    isLoading,
    isError,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useBizData
    ? { ...bizUsers, isFetchingNextPage: false, hasNextPage: false, fetchNextPage: async () => {} }
    : adminUsers;

  // Fetch biz roles so we can show the assigned role name in the table.
  // Reuses the same query key as the Edit drawer, so it's served from cache.
  const { data: rolesData } = useQuery({
    queryKey: ["biz-roles", bizId],
    queryFn: () => bizRoleApi.list(bizId),
    enabled: useBizData && !!bizId,
  });
  const roleNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of rolesData?.items ?? []) map.set(r.roleId, r.name);
    return map;
  }, [rolesData]);

  const deactivate = useDeactivateUser();
  const reactivate = useReactivateUser();
  const { toasts, toast, dismissToast } = useToast();

  const [showAddModal, setShowAddModal] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [showAddExisting, setShowAddExisting] = useState(false);
  const [editUser, setEditUser] = useState<EmployeeProfile | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<EmployeeProfile | null>(null);
  const [historyTarget, setHistoryTarget] = useState<EmployeeProfile | null>(null);

  // Flatten all loaded pages; exclude self when using biz-scoped data.
  // biz-scoped endpoint returns all members at once (no server-side search),
  // so filter client-side. Global endpoint handles search server-side.
  const users: EmployeeProfile[] = useBizData
    ? (() => {
        const all = (bizUsers.data ?? []).filter((u) => u.userId !== authUser?.userId);
        if (!search) return all;
        const q = search.toLowerCase();
        return all.filter((u) =>
          `${u.firstName} ${u.lastName}`.toLowerCase().includes(q) ||
          (u.username ?? "").toLowerCase().includes(q) ||
          (u.email ?? "").toLowerCase().includes(q) ||
          (u.department ?? "").toLowerCase().includes(q) ||
          (u.position ?? "").toLowerCase().includes(q),
        );
      })()
    : (adminUsers.data?.pages.flatMap((p) => p.items) ?? []);

  const activeCount = users.filter((u) => u.status === "ACTIVE").length;
  const inactiveCount = users.filter((u) => u.status === "INACTIVE").length;

  const handleToggleStatus = async () => {
    if (!confirmTarget) return;
    try {
      if (confirmTarget.status === "ACTIVE") {
        await deactivate.mutateAsync(confirmTarget.userId);
        toast.success(`${confirmTarget.firstName} ${confirmTarget.lastName} has been deactivated.`);
        if (bizId) {
          void auditApi.log(bizId, {
            entityType: "member",
            entityId: confirmTarget.userId,
            action: "deactivate-member",
            page: "admin:users",
            metadata: { name: `${confirmTarget.firstName} ${confirmTarget.lastName}` },
          });
        }
      } else {
        await reactivate.mutateAsync(confirmTarget.userId);
        toast.success(`${confirmTarget.firstName} ${confirmTarget.lastName} has been reactivated.`);
        if (bizId) {
          void auditApi.log(bizId, {
            entityType: "member",
            entityId: confirmTarget.userId,
            action: "reactivate-member",
            page: "admin:users",
            metadata: { name: `${confirmTarget.firstName} ${confirmTarget.lastName}` },
          });
        }
      }
    } catch {
      toast.error("Action failed. Please try again.");
    } finally {
      setConfirmTarget(null);
    }
  };

  return (
    <>
      <div className="space-y-4">
        {/* ── Header card ───────────────────────────────────────────────────── */}
        <div className="rounded-xl border border-[#e2e8f0] bg-white px-5 py-4 shadow-sm space-y-3">
          {/* Title row */}
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#2447d9]">
              <Users className="h-4 w-4 text-white" />
            </div>
            <div>
              <h1 className="text-base font-bold text-[#0f172a]">Manage Users</h1>
              <p className="text-xs text-[#64748b]">Create and manage employee accounts and access</p>
            </div>
          </div>

          {/* Toolbar: stats + search + Add User */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Status pills */}
            <div className="flex items-center gap-1.5 rounded-full border border-[#bbf7d0] bg-[#f0fdf4] px-3 py-1">
              <span className="h-1.5 w-1.5 rounded-full bg-[#16a34a]" />
              <span className="text-xs text-[#16a34a] font-medium">{activeCount} Active</span>
            </div>
            {inactiveCount > 0 && (
              <div className="flex items-center gap-1.5 rounded-full border border-[#fecaca] bg-[#fef2f2] px-3 py-1">
                <span className="h-1.5 w-1.5 rounded-full bg-[#dc2626]" />
                <span className="text-xs text-[#dc2626] font-medium">{inactiveCount} Inactive</span>
              </div>
            )}

            {/* Separator */}
            <div className="h-4 w-px bg-[#e2e8f0] hidden sm:block" />

            {/* Search — flex-1 so it fills available space */}
            <div className="relative flex-1 min-w-[160px]">
              <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#94a3b8]" />
              <input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search name, username, email…"
                className="w-full rounded-lg border border-[#e2e8f0] bg-[#f8fafc] pl-8 pr-3 py-1.5 text-sm text-[#0f172a] placeholder:text-[#94a3b8] focus:bg-white focus:border-[#2447d9] focus:outline-none focus:ring-2 focus:ring-[#2447d9]/20"
              />
            </div>

            {/* Add User dropdown */}
            <div className="relative shrink-0">
              <button
                onClick={() => setShowAddMenu((s) => !s)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-[#2447d9] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#1e3a8a] transition-colors"
              >
                <Plus className="h-4 w-4" />
                Add User
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showAddMenu ? "rotate-180" : ""}`} />
              </button>
              {showAddMenu && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowAddMenu(false)} aria-hidden="true" />
                  <div className="absolute right-0 z-20 mt-1 w-[min(240px,calc(100vw-2rem))] rounded-xl border border-[#e2e8f0] bg-white p-1 shadow-[0_4px_16px_0_rgb(0_0_0/0.12)]">
                    <button
                      onClick={() => { setShowAddModal(true); setShowAddMenu(false); }}
                      className="flex w-full items-start gap-2.5 rounded-lg px-3 py-2 text-left hover:bg-[#f8fafc] transition-colors"
                    >
                      <UserPlus className="h-4 w-4 shrink-0 text-[#2447d9] mt-0.5" />
                      <span>
                        <span className="block text-sm font-medium text-[#0f172a]">Invite new employee</span>
                        <span className="block text-xs text-[#94a3b8]">Create a new account &amp; send an invite</span>
                      </span>
                    </button>
                    <button
                      onClick={() => { setShowAddExisting(true); setShowAddMenu(false); }}
                      disabled={!bizId}
                      className="flex w-full items-start gap-2.5 rounded-lg px-3 py-2 text-left hover:bg-[#f8fafc] transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                    >
                      <UserCog className="h-4 w-4 shrink-0 text-[#2447d9] mt-0.5" />
                      <span>
                        <span className="block text-sm font-medium text-[#0f172a]">Add existing employee</span>
                        <span className="block text-xs text-[#94a3b8]">
                          {bizId ? "Grant an existing employee access here" : "Select a business in the top bar first"}
                        </span>
                      </span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ── Table / States ─────────────────────────────────────────────── */}
        <div className="rounded-2xl border border-[#e2e8f0] bg-white overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <LoadingSpinner size="lg" className="text-[#2447d9]" />
            </div>
          ) : isError ? (
            <div className="flex items-center justify-center py-20">
              <EmptyState
                title="Failed to load users"
                description="Something went wrong. Please refresh the page."
                className="py-0"
              />
            </div>
          ) : users.length === 0 ? (
            <div className="flex items-center justify-center py-20">
              <EmptyState
                icon={<Users className="h-6 w-6" />}
                title={search ? "No users match your search" : "No users yet"}
                description={
                  search
                    ? "Try a different search term."
                    : "Click \"Add User\" to create the first employee account."
                }
                action={
                  !search
                    ? { label: "Add User", onClick: () => setShowAddModal(true) }
                    : undefined
                }
                className="py-0"
              />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#e2e8f0] bg-[#f8fafc]">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                        Employee
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                        Username
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748b] uppercase tracking-wide hidden md:table-cell">
                        Position
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748b] uppercase tracking-wide hidden lg:table-cell">
                        Department
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748b] uppercase tracking-wide hidden xl:table-cell">
                        Hire Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748b] uppercase tracking-wide hidden xl:table-cell">
                        Salary
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                        Role
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                        Status
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#f1f5f9]">
                    {users.map((user) => (
                      <tr
                        key={user.userId}
                        className="hover:bg-[#f8fafc] transition-colors"
                      >
                        {/* Name + email */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#eff3ff] text-[#2447d9] text-xs font-semibold">
                              {user.firstName[0]?.toUpperCase()}
                              {user.lastName[0]?.toUpperCase()}
                            </div>
                            <div>
                              <div className="flex items-center gap-1.5">
                                <p className="font-medium text-[#0f172a]">
                                  {user.firstName} {user.lastName}
                                </p>
                                {(user.isStoreManager || user.isSalonStaff) && (
                                  <span title="Health & Beauty — Manage Store access" className="flex items-center gap-0.5 rounded-full bg-[#eff3ff] px-1.5 py-0.5 text-[10px] font-semibold text-[#2447d9]">
                                    <Scissors className="h-2.5 w-2.5" />
                                    Store
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-[#94a3b8]">{user.email}</p>
                            </div>
                          </div>
                        </td>
                        {/* Username */}
                        <td className="px-4 py-3 text-[#64748b]">@{user.username}</td>
                        {/* Position */}
                        <td className="px-4 py-3 text-[#64748b] hidden md:table-cell">
                          {user.position}
                        </td>
                        {/* Department */}
                        <td className="px-4 py-3 text-[#64748b] hidden lg:table-cell">
                          {user.department}
                        </td>
                        {/* Hire Date */}
                        <td className="px-4 py-3 text-[#64748b] hidden xl:table-cell">
                          {formatDate(user.hireDate)}
                        </td>
                        {/* Salary */}
                        <td className="px-4 py-3 text-[#0f172a] font-medium hidden xl:table-cell">
                          {formatCurrency(user.baseSalary)}
                        </td>
                        {/* Role badge */}
                        <td className="px-4 py-3">
                          {useBizData ? (
                            user.isBusinessAdmin ? (
                              <Badge variant="info">Biz Admin</Badge>
                            ) : user.roleId && roleNameById.get(user.roleId) ? (
                              <Badge variant="default">{roleNameById.get(user.roleId)}</Badge>
                            ) : (
                              <span className="text-xs text-[#94a3b8]">No role</span>
                            )
                          ) : (
                            <Badge variant={user.role === "super-admin" ? "info" : "default"}>
                              {user.role === "super-admin" ? "Admin" : "Employee"}
                            </Badge>
                          )}
                        </td>
                        {/* Status badge */}
                        <td className="px-4 py-3">
                          <Badge variant={user.status === "ACTIVE" ? "success" : "danger"}>
                            {user.status}
                          </Badge>
                        </td>
                        {/* Actions */}
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => setEditUser(user)}
                              title="Edit employee"
                              className="flex h-7 w-7 items-center justify-center rounded-md text-[#64748b] hover:bg-[#eff3ff] hover:text-[#2447d9] transition-colors"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={() => setConfirmTarget(user)}
                              title={user.status === "ACTIVE" ? "Deactivate" : "Reactivate"}
                              className={[
                                "flex h-7 w-7 items-center justify-center rounded-md transition-colors",
                                user.status === "ACTIVE"
                                  ? "text-[#64748b] hover:bg-[#fef2f2] hover:text-[#dc2626]"
                                  : "text-[#64748b] hover:bg-[#f0fdf4] hover:text-[#16a34a]",
                              ].join(" ")}
                            >
                              {user.status === "ACTIVE" ? (
                                <UserX className="h-3.5 w-3.5" />
                              ) : (
                                <UserCheck className="h-3.5 w-3.5" />
                              )}
                            </button>
                            {bizId && (
                              <button
                                onClick={() => setHistoryTarget(user)}
                                title="View change history"
                                className="flex h-7 w-7 items-center justify-center rounded-md text-[#64748b] hover:bg-[#eff3ff] hover:text-[#2447d9] transition-colors"
                              >
                                <History className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Load more */}
              {(hasNextPage || isFetchingNextPage) && (
                <div className="flex items-center justify-center border-t border-[#e2e8f0] p-4">
                  <button
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                    className="inline-flex items-center gap-2 rounded-lg border border-[#e2e8f0] bg-white px-5 py-2 text-sm font-medium text-[#374151] hover:bg-[#f8fafc] transition-colors disabled:opacity-60"
                  >
                    {isFetchingNextPage ? (
                      <>
                        <LoadingSpinner size="sm" className="text-[#2447d9]" />
                        Loading…
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-4 w-4" />
                        Load more
                      </>
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Invite New Employee Modal */}
      <AddUserModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        businessId={useBizData ? bizId : undefined}
        onSuccess={() => {
          setShowAddModal(false);
          toast.success("User created successfully. An invite email has been sent.");
          bizUsers.refetch();
          adminUsers.refetch();
        }}
      />

      {/* Add Existing Employee Modal (grant access to a picked business) */}
      {bizId && (
        <AddExistingMemberModal
          isOpen={showAddExisting}
          onClose={() => setShowAddExisting(false)}
          businessId={bizId}
          onSuccess={() => {
            setShowAddExisting(false);
            toast.success("Access granted. The employee can now switch into this business.");
            bizUsers.refetch();
          }}
        />
      )}

      {/* Edit User Drawer */}
      <EditUserDrawer
        isOpen={editUser !== null}
        onClose={() => setEditUser(null)}
        user={editUser}
        businessId={useBizData ? bizId : undefined}
        onSuccess={(changes) => {
          if (bizId && editUser) {
            void auditApi.log(bizId, {
              entityType: "member",
              entityId: editUser.userId,
              action: "update-member",
              page: "admin:users",
              changes,
            });
          }
          setEditUser(null);
          toast.success("Employee details updated.");
        }}
      />

      {/* Confirm deactivate/reactivate */}
      <ConfirmModal
        isOpen={confirmTarget !== null}
        onClose={() => setConfirmTarget(null)}
        onConfirm={handleToggleStatus}
        isPending={deactivate.isPending || reactivate.isPending}
        isDanger={confirmTarget?.status === "ACTIVE"}
        title={
          confirmTarget?.status === "ACTIVE"
            ? "Deactivate Employee"
            : "Reactivate Employee"
        }
        description={
          confirmTarget?.status === "ACTIVE"
            ? `Are you sure you want to deactivate ${confirmTarget?.firstName} ${confirmTarget?.lastName}? They will lose access to the system.`
            : `Reactivate ${confirmTarget?.firstName} ${confirmTarget?.lastName}? They will regain access to the system.`
        }
        confirmLabel={
          confirmTarget?.status === "ACTIVE" ? "Deactivate" : "Reactivate"
        }
      />

      {/* Toast container */}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />

      {/* Change history modal — per-business member audit */}
      {bizId && historyTarget && (
        <AuditHistoryModal
          isOpen={historyTarget !== null}
          onClose={() => setHistoryTarget(null)}
          businessId={bizId}
          entityType="member"
          entityId={historyTarget.userId}
          label={`${historyTarget.firstName} ${historyTarget.lastName}`}
        />
      )}
    </>
  );
}
